import axios from 'axios';
import Config from '../../../../config';
import { DocumentHelper } from '../../../../helpers';
import { ORDER_STATUS } from '../constants';
import { BulkActionOrder } from '../types';

/**
 * Service for managing order-related operations
 * Handles fetching, updating, and bulk operations on orders
 */
class OrderService {
  /**
   * Fetch orders with the given configuration and filters
   * @param config - Table configuration including pagination, sorting, etc.
   * @param filters - Applied filters for order search
   * @returns Orders data with results and metadata
   * @throws {Error} If the API request fails
   */
  async fetchOrders(config: Record<string, unknown>, filters: unknown[]): Promise<any> {
    try {
      const url = `${Config.api}/odata/Company/Functions.DocumentSearch`;
      const params = {
        ...config,
        filter: { logic: 'and', filters }
      };

      const response = await axios.post(url, params);
      return response.data;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  }

  /**
   * Get detailed information for a specific order
   * @param orderId - The unique identifier of the order
   * @returns Order details including line items, addresses, and metadata
   * @throws {Error} If the order is not found or API request fails
   */
  async getOrderDetails(orderId: string | number): Promise<any> {
    try {
      const response = await axios.get(
        `${Config.api}/api/v1/orders/${orderId}`
      );
      return response.data.Body;
    } catch (error) {
      console.error('Error fetching order details:', error);
      throw error;
    }
  }

  /**
   * Update the status of an order
   * @param orderId - The unique identifier of the order to update
   * @param status - New status code from ORDER_STATUS constants
   * @returns Updated order object with new status
   * @throws {Error} If the status update fails or order not found
   */
  async updateOrderStatus(orderId: string | number, status: number): Promise<any> {
    try {
      const response = await axios.patch(
        `${Config.api}/api/v1/orders/${orderId}/status`,
        { status }
      );
      return response.data.Body;
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  }

  /**
   * Bulk update document status for multiple orders
   * @param documents - Array of document objects to update
   * @param status - New status code to apply to all documents
   * @returns Resolves when all documents are updated
   * @throws {Error} If bulk update operation fails
   */
  async bulkUpdateDocuments(documents: BulkActionOrder[], status: number): Promise<void> {
    try {
      const documentsToUpdate = documents.map(doc => ({
        ...doc,
        documentType: 'Order'
      }));

      await new DocumentHelper().bulkUpdateDocuments(documentsToUpdate, status);
    } catch (error) {
      console.error('Error bulk updating documents:', error);
      throw error;
    }
  }

  /**
   * Retry failed orders by resetting their status
   * @param orders - Array of order objects to retry
   * @returns Resolves when all failed orders are reset to retry
   * @throws {Error} If retry operation fails
   * @description Only processes orders with FAILED status, resets them to status 0 for reprocessing
   */
  async retryOrders(orders: BulkActionOrder[]): Promise<void> {
    const failedOrders = orders.filter(o => o.status === ORDER_STATUS.FAILED);
    return this.bulkUpdateDocuments(failedOrders, 0);
  }

  /**
   * Mark failed orders as ignored
   * @param orders - Array of order objects to ignore
   * @returns Resolves when all failed orders are marked as ignored
   * @throws {Error} If ignore operation fails
   * @description Only processes orders with FAILED status, marks them as IGNORED
   */
  async ignoreOrders(orders: BulkActionOrder[]): Promise<void> {
    const failedOrders = orders.filter(o => o.status === ORDER_STATUS.FAILED);
    return this.bulkUpdateDocuments(failedOrders, ORDER_STATUS.IGNORED);
  }

  /**
   * Cancel orders regardless of their current status
   * @param orders - Array of order objects to cancel
   * @returns Resolves when all orders are cancelled
   * @throws {Error} If cancel operation fails
   * @description Updates all provided orders to CANCELLED status
   */
  async cancelOrders(orders: BulkActionOrder[]): Promise<void> {
    return this.bulkUpdateDocuments(orders, ORDER_STATUS.CANCELLED);
  }
}

const orderService = new OrderService();
export default orderService;